import { Card, Divider, Text, TextButton, Title } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";
import { type Control, Controller, type UseFormSetValue } from "react-hook-form";
import SignaturePad from "react-signature-pad-wrapper";

import { type TimesheetFormValues } from "../types/timesheetFormTypes";

interface WorkerSignatureSectionProps {
  control: Control<TimesheetFormValues>;
  firstName: string;
  fullName: string;
  workerStation: string;
  workerNursingAssignment: string;
  signaturePadIsDisabled: boolean;
  setValue: UseFormSetValue<TimesheetFormValues>;
}

export function WorkerSignatureSection(props: WorkerSignatureSectionProps) {
  const {
    control,
    firstName,
    fullName,
    workerStation,
    workerNursingAssignment,
    signaturePadIsDisabled,
    setValue,
  } = props;

  const signaturePadRef = useRef<SignaturePad>(null);

  const onSignatureUpdate = useCallback(() => {
    const signature = signaturePadRef.current?.toDataURL();
    if (signature) {
      setValue("hcpSignature", signature, { shouldValidate: true });
    }
  }, [setValue]);

  useEffect(() => {
    const signaturePad = signaturePadRef.current;
    signaturePad?.instance?.addEventListener("endStroke", onSignatureUpdate);
    return () => {
      signaturePad?.instance?.removeEventListener("endStroke", onSignatureUpdate);
    };
  }, [onSignatureUpdate]);

  const clearSignature = () => {
    signaturePadRef.current?.clear();
    setValue("hcpSignature", "", { shouldValidate: true });
  };

  return (
    <Stack
      spacing={6}
      sx={{ paddingX: 6, paddingY: 6, backgroundColor: (theme) => theme.palette.background.paper }}
    >
      <Title bold variant="h4" component="h1">
        {firstName}, please sign below
      </Title>

      <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
        <Text variant="body2">Station / Wing / Unit / Floor*</Text>
        <Text semibold variant="body1">
          {workerStation}
        </Text>
      </Stack>

      <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
        <Text variant="body2">Nursing services assignment*</Text>
        <Text semibold variant="body1">
          {workerNursingAssignment}
        </Text>
      </Stack>

      <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
        <Text variant="body2">Your full name*</Text>
        <Text semibold variant="body1">
          {fullName}
        </Text>
      </Stack>

      {/* HCP Signature */}
      <Stack spacing={2}>
        <Text variant="body2">{fullName}&apos;s signature*</Text>
        <Card outlined sx={{ p: 5 }}>
          <Controller
            name="hcpSignature"
            control={control}
            render={() => {
              return (
                <Box aria-label="Worker signature">
                  <Box sx={{ pointerEvents: signaturePadIsDisabled ? "none" : "all" }}>
                    <SignaturePad ref={signaturePadRef} redrawOnResize height={180} />
                  </Box>
                </Box>
              );
            }}
          />
          <Divider sx={{ borderColor: (theme) => theme.palette.border?.primary, mb: 4 }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <TextButton underline={false} LinkComponent="button" onClick={clearSignature}>
              Clear
            </TextButton>
          </Box>
        </Card>
      </Stack>
    </Stack>
  );
}
